import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { Typography, FormControlLabel, Checkbox, Box } from '@mui/material';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import {
  useDataProvider,
  DatePicker,
  TimePicker,
  TextArea,
  useUserAccess,
  FieldWrapperVertical,
  SnackbarApiErrorMessage,
  useLocalization,
  useDialog,
  NumericTextField,
  useSnackbar,
  SubSection,
} from '@ui-framework/index';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import { nanoid } from 'nanoid';
import AcceptanceLocationFormSection from './AcceptanceLocationFormSection';
import PendingAcceptanceDialogBody from './PendingAcceptanceDialogBody';
import DialogCloseButton from './DialogCloseButton';
import IconLibrary from '../IconLibrary';
import {
  getDispatchEventLocation,
  toLocationOption,
} from '../FormFields/ServiceProviderRecommendedLocations';

const SERVICE_PROVIDER_LOCATION_ID_PARAM = 'locationId';

function ConditionallyAcceptFormInner({
  validationSchema,
  defaultValues,
  dispatchEventId,
  dispatchId,
  formatMessage,
  onSubmit,
  onCancel,
}) {
  const methods = useForm({
    defaultValues,
    resolver: yupResolver(validationSchema),
  });
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    watch,
    trigger,
    clearErrors,
  } = methods;

  const [differentDay, moreMoney, other] = watch([
    'differentDay',
    'moreMoney',
    'other',
  ]);

  const atleaastOneRequiredError = errors?.checkboxGroup?.message || false;

  useEffect(() => {
    if (!differentDay) {
      methods.setValue('appointmentDate');
      methods.setValue('serviceDateAfterHour');
      methods.setValue('serviceDateBeforeHour');
    }
    if (!moreMoney) {
      methods.setValue('additionalFee');
    }
    if (!other) {
      methods.setValue('additionalConditionDescription');
    }
  }, [differentDay, moreMoney, other]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <>
            <input type="hidden" {...register('checkboxGroup')} />

            <AcceptanceLocationFormSection
              dispatchEventId={dispatchEventId}
              dispatchId={dispatchId}
              errors={errors}
              serviceJobId={defaultValues?.serviceJobId}
              name="acceptanceLocation"
            />
            <Typography variant="body2" sx={{ mb: 2, pt: 1 }}>
              Please select and update atleast one or more conditions
            </Typography>
            <SubSection showError error={atleaastOneRequiredError}>
              <Box>
                {atleaastOneRequiredError && (
                  <Typography color="error" sx={{ mb: 2 }}>
                    {errors?.checkboxGroup?.message}
                  </Typography>
                )}
              </Box>
              <Grid2 container spacing={1}>
                <Grid2 xs={12}>
                  <Box>
                    <Controller
                      name="differentDay"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              {...field}
                              checked={field.value}
                              onChange={e => {
                                field.onChange(e.target.checked);
                                if (!e.target.checked) {
                                  clearErrors([
                                    'appointmentDate',
                                    'serviceDateAfterHour',
                                    'serviceDateBeforeHour',
                                  ]);
                                }
                                trigger('checkboxGroup');
                              }}
                            />
                          }
                          label="Different Day"
                        />
                      )}
                    />
                  </Box>
                  <Grid2 container spacing={0.5}>
                    <Grid2 xs={4}>
                      <FieldWrapperVertical
                        field={{
                          name: 'appointmentDate',
                          label: {
                            localeId: 'SERVICE_DATE_LABEL',
                            defaultMessage: 'Service Date',
                          },
                        }}
                        errors={errors}>
                        <DatePicker
                          name="appointmentDate"
                          disabled={!differentDay}
                          disablePast
                        />
                      </FieldWrapperVertical>
                    </Grid2>
                    <Grid2 xs={4}>
                      <Controller
                        name="serviceDateAfterHour"
                        control={control}
                        render={({ field }) => (
                          <FieldWrapperVertical
                            field={{
                              name: 'serviceDateAfterHour',
                              label: {
                                localeId: 'SERVICE_DATE_AFTER_HOUR_LABEL',
                                defaultMessage: 'After',
                              },
                            }}>
                            <TimePicker {...field} disabled={!differentDay} />
                          </FieldWrapperVertical>
                        )}
                      />
                    </Grid2>
                    <Grid2 xs={4}>
                      <Controller
                        name="serviceDateBeforeHour"
                        control={control}
                        render={({ field }) => (
                          <FieldWrapperVertical
                            field={{
                              name: 'serviceDateBeforeHour',
                              label: {
                                localeId: 'SERVICE_DATE_BEFORE_HOUR_LABEL',
                                defaultMessage: 'Before',
                              },
                            }}>
                            <TimePicker {...field} disabled={!differentDay} />
                          </FieldWrapperVertical>
                        )}
                      />
                    </Grid2>
                  </Grid2>
                </Grid2>
                <Grid2 xs={12}>
                  <Box>
                    <Controller
                      name="moreMoney"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              {...field}
                              checked={field.value}
                              onChange={e => {
                                field.onChange(e.target.checked);
                                if (!e.target.checked) {
                                  clearErrors(['additionalFee']);
                                }
                                trigger('checkboxGroup');
                              }}
                            />
                          }
                          label="More Money"
                        />
                      )}
                    />
                  </Box>
                  <Box>
                    <FieldWrapperVertical
                      field={{
                        name: 'additionalFee',
                        label: {
                          localeId: 'ADDITIONAL_FEE_LABEL',
                          defaultMessage: 'Additional Fee',
                        },
                      }}
                      errors={errors}>
                      <NumericTextField
                        {...register('additionalFee')}
                        fixedDecimalScale={2}
                        positiveNumber
                        startAdornment="$"
                        disabled={!moreMoney}
                        error={!!errors?.additionalFee}
                        helperText={errors?.additionalFee?.message}
                      />
                    </FieldWrapperVertical>
                  </Box>
                </Grid2>
                <Grid2 xs={12}>
                  <Box>
                    <Controller
                      name="other"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              {...field}
                              checked={field.value}
                              onChange={e => {
                                field.onChange(e.target.checked);
                                if (!e.target.checked) {
                                  clearErrors([
                                    'additionalConditionDescription',
                                  ]);
                                }
                                trigger('checkboxGroup');
                              }}
                            />
                          }
                          label="Other"
                        />
                      )}
                    />
                  </Box>
                  <Box>
                    <FieldWrapperVertical
                      field={{
                        name: 'additionalConditionDescription',
                        label: {
                          localeId: 'ADDITIONAL_CONDITOIN_DESCRIPTION_LABEL',
                          defaultMessage: 'Additional Condition Description',
                        },
                      }}
                      errors={errors}>
                      <TextArea
                        {...register('additionalConditionDescription')}
                        disabled={!other}
                        error={
                          !!errors?.additionalConditionDescription?.message
                        }
                        helperText={
                          errors?.additionalConditionDescription?.message
                        }
                      />
                    </FieldWrapperVertical>
                  </Box>
                </Grid2>
              </Grid2>
            </SubSection>
          </>
        </DialogContent>
        <DialogActions display="flex" sx={{ justifyContent: 'center', my: 2 }}>
          <Button
            type="Submit"
            variant="contained"
            color="primary"
            startIcon={<IconLibrary.Accept />}>
            {formatMessage({
              id: 'CONDITIONALLY ACCEPT',
              defaultMessage: 'Conditionally Accept',
            })}
          </Button>
          <Button
            type="button"
            variant="contained"
            color="secondary"
            startIcon={<IconLibrary.Cancel />}
            onClick={onCancel}>
            {formatMessage({ id: 'CANCEL', defaultMessage: 'Cancel' })}
          </Button>
        </DialogActions>
      </form>
    </FormProvider>
  );
}

function ConditionallyAcceptJobDialog({
  open,
  onClose,
  jobInfo,
  dispatchEventId,
}) {
  const userAccess = useUserAccess();
  const { formatMessage } = useLocalization();
  const snackbar = useSnackbar();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { getApiUrl, put } = useDataProvider(provider);

  const { get: getDispatchAdvisor, getApiUrl: getDispatchAdvisorApiUrl } =
    useDataProvider('dispatchAdvisor');

  const serviceProviderId = userAccess?.company?.id;
  const { confirmationRef } = useDialog();
  const { serviceJobNumber = '' } = jobInfo;
  const dialogDispatchEventId = dispatchEventId || jobInfo?.dispatchEvent?.id;
  const dialogDispatchId = jobInfo?.dispatchEvent?.dispatchId;

  const [loading, setLoading] = useState(true);
  const [initialValues, setInitialValues] = useState({
    differentDay: false,
    moreMoney: false,
    other: false,
    serviceJobId: jobInfo?.id,
    serviceProviderId,
    acceptanceLocation: null,
    serviceDateAfterHour: null,
    serviceDateBeforeHour: null,
    additionalConditionDescription: '',
  });

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        differentDay: yup.boolean(),
        moreMoney: yup.boolean(),
        other: yup.boolean(),
        acceptanceLocation: yup
          .mixed()
          .required(
            formatMessage({
              id: 'ACCEPTANCE_LOCATION_REQUIRED',
              defaultMessage: 'Location is required',
            }),
          )
          .test(
            'acceptance-location-value',
            formatMessage({
              id: 'ACCEPTANCE_LOCATION_REQUIRED',
              defaultMessage: 'Location is required',
            }),
            val =>
              val != null &&
              typeof val === 'object' &&
              !Array.isArray(val) &&
              val.value != null &&
              val.value !== '',
          ),
        checkboxGroup: yup.mixed().test(
          'at-least-one-checked',
          formatMessage({
            id: 'AT_LEAST_ONE_CONDITION_REQUIRED',
            defaultMessage: 'At least one condition must be selected',
          }),
          (_, context) => {
            const {
              differentDay: dd,
              moreMoney: mm,
              other: ot,
            } = context.parent;
            return !!(dd || mm || ot);
          },
        ),
        appointmentDate: yup.string().when('differentDay', {
          is: true,
          then: schema =>
            schema
              .required(
                formatMessage({
                  id: 'APPOINTMENT_DATE_REQUIRED',
                  defaultMessage: 'Date is required',
                }),
              )
              .test(
                'is-date-in-past',
                formatMessage({
                  id: 'APPOINTMENT_DATE_PAST',
                  defaultMessage: 'Date cannot be in the past',
                }),
                value => !dayjs(value).isBefore(dayjs(), 'day'),
              ),
          otherwise: schema => schema.notRequired(),
        }),
        additionalFee: yup.string().when('moreMoney', {
          is: true,
          then: schema =>
            schema
              .required(
                formatMessage({
                  id: 'ADDITIONAL_FEE_REQUIRED',
                  defaultMessage: 'Additional fee is required',
                }),
              )
              .test(
                'is-greater-than-one',
                formatMessage({
                  id: 'ADDITIONAL_FEE_MIN',
                  defaultMessage: 'Additional fee must be greater than 1',
                }),
                value => parseFloat(value) >= 1,
              ),
          otherwise: schema => schema.notRequired(),
        }),
        additionalConditionDescription: yup.string().when('other', {
          is: true,
          then: schema =>
            schema.required(
              formatMessage({
                id: 'CONDITIONAL_DESCRIPTION_REQUIRED',
                defaultMessage: 'Additional Condition Description is required',
              }),
            ),
          otherwise: schema => schema.notRequired(),
        }),
      }),
    [formatMessage],
  );

  const onCancel = useCallback(() => onClose(null), [onClose]);

  const jobInfoRef = useRef(jobInfo);
  jobInfoRef.current = jobInfo;
  const formMountKey = `${jobInfo?.id ?? ''}-${jobInfo?.dispatchEvent?.id ?? ''}`;

  const handleConditionalSubmit = useCallback(
    async data => {
      setLoading(true);
      const dispatchEventValue =
        dialogDispatchEventId || jobInfoRef.current?.dispatchEvent?.id;
      if (!dispatchEventValue) {
        snackbar.showMessage({
          type: 'error',
          data: formatMessage({
            id: 'ACCEPT_CONDITIONALLY_FAILURE',
            defaultMessage: 'Error: Could not conditionally accept the job',
          }),
        });
        setLoading(false);
        return;
      }

      const locationId = data?.acceptanceLocation?.value ?? null;
      if (!locationId) {
        setLoading(false);
        return;
      }

      const body = {
        dispatchEventId: dispatchEventValue,
        serviceDate: data.appointmentDate || null,
        additionalMoney: data.additionalFee || null,
        serviceDateAfterHour: data.serviceDateAfterHour || null,
        serviceDateBeforeHour: data.serviceDateBeforeHour || null,
        reason: data.additionalConditionDescription || null,
      };

      const query = new URLSearchParams();
      query.set(SERVICE_PROVIDER_LOCATION_ID_PARAM, locationId);
      const jobId = jobInfoRef.current?.id;
      const response = await put(
        `${getApiUrl()}/sxcl/v1/service-providers/${serviceProviderId}/pending-acceptance/${jobId}/accept-conditionally?${query.toString()}`,
        body,
      );
      if (response.ok) {
        confirmationRef.current.init({
          message: 'Job was conditionally accepted successfully',
          positiveButtonText: 'Close',
          width: 360,
          title: 'Job Conditionally Accepted',
          onChange: () => {
            confirmationRef.current.setOpen(false);
            onClose(nanoid());
          },
        });
        confirmationRef.current.setOpen(true);
      } else {
        snackbar.showMessage({
          type: 'error',
          data: <SnackbarApiErrorMessage errors={response?.errors} />,
        });
      }
      setLoading(false);
    },
    [
      confirmationRef,
      dialogDispatchEventId,
      formatMessage,
      getApiUrl,
      onClose,
      put,
      snackbar,
      serviceProviderId,
    ],
  );

  const initialize = async () => {
    setLoading(true);
    const dispatchEventLocation = await getDispatchEventLocation({
      serviceJobId: jobInfo?.id,
      dispatchId: dialogDispatchId,
      dispatchEventId: dialogDispatchEventId,
      apiUrl: getDispatchAdvisorApiUrl(),
      get: getDispatchAdvisor,
      snackbar,
    });
    const dispatch = jobInfo?.dispatchEvent;
    let updatedValuesIfDispatch = {};
    if (dispatch?.id) {
      updatedValuesIfDispatch = {
        differentDay: !!dispatch?.serviceDate,
        appointmentDate: dispatch?.serviceDate || '',
        serviceDateAfterHour: dispatch?.serviceDateAfterHour || null,
        serviceDateBeforeHour: dispatch?.serviceDateBeforeHour || null,
        moreMoney: !!dispatch?.additionalMoney,
        additionalFee: dispatch?.additionalMoney || '',
        other:
          dispatch?.status === 'ACCEPTED_WITH_CONDITIONS'
            ? !!dispatch?.reason
            : false,
        additionalConditionDescription:
          dispatch?.status === 'ACCEPTED_WITH_CONDITIONS'
            ? dispatch?.reason || ''
            : '',
      };
    }

    setInitialValues(prev => ({
      ...prev,
      ...updatedValuesIfDispatch,
      acceptanceLocation: toLocationOption(dispatchEventLocation),
    }));
    setLoading(false);
  };

  useEffect(() => {
    initialize();
  }, []);

  return (
    <Dialog
      open={open}
      onClose={() => {
        onClose(null);
      }}
      maxWidth={false}
      PaperProps={{
        sx: { width: 750, maxWidth: 850 },
      }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconLibrary.ConditionllyAcceptServiceJob />
        {formatMessage({
          id: 'CONDITIONALLY_ACCEPT_JOB',
          defaultMessage: 'Conditionally Accept Job',
        })}
        &nbsp;&ndash;
        <Typography color="primary" variant="body">
          {serviceJobNumber}
        </Typography>
        <DialogCloseButton
          disabled={loading}
          onClose={() => {
            onClose(null);
          }}
        />
      </DialogTitle>
      <PendingAcceptanceDialogBody loading={loading}>
        <ConditionallyAcceptFormInner
          key={formMountKey}
          validationSchema={validationSchema}
          defaultValues={initialValues}
          dispatchEventId={dialogDispatchEventId}
          dispatchId={dialogDispatchId}
          formatMessage={formatMessage}
          onSubmit={handleConditionalSubmit}
          onCancel={onCancel}
        />
      </PendingAcceptanceDialogBody>
    </Dialog>
  );
}

export default ConditionallyAcceptJobDialog;
